import React from 'react';
import { Upload, Video, FileText, X, Sparkles } from 'lucide-react';

// ==================== Team Member Form ====================

export const TeamMemberForm = ({
  formData,
  setFormData,
  editingIndex,
  onSubmit,
  onCancel,
  onVideoUpload,
  onSubtitleUpload,
  onRemoveVideo,
  onRemoveSubtitle,
  onGenerateSubtitles,
  generatingSubtitles,
  uploadingVideo,
  uploadingSubtitle,
  uploadProgress,
  removingVideo,
  removingSubtitle,
  videoInputRef
}) => {
  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        // Only close if clicking the backdrop itself, not the modal content
        if (e.target === e.currentTarget) {
          onCancel();
        }
      }}
    >
      <div 
        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900">
              {editingIndex !== null ? 'Edit Team Member' : 'Create New Team Member'}
            </h2>
            <button type="button" onClick={onCancel} className="p-2 text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-5">
          {/* Name & Role */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
                className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Role *</label>
              <input
                type="text"
                value={formData.role}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                required
                className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
            </div>
          </div>

          {/* Team Category */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Team Category *</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
            >
              <option value="Management Team">Management Team</option>
              <option value="Teachers' Team">Teachers&apos; Team</option>
            </select>
          </div>

          {/* Display Order */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Display Order</label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
              min="0"
              className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
            <p className="text-xs text-slate-400 mt-1">Lower numbers appear first</p>
          </div>

          {/* Bio Fields */}
          <BioFields formData={formData} setFormData={setFormData} />

          {/* Video Upload Section */}
          <VideoUploadSection
            formData={formData}
            setFormData={setFormData}
            onVideoUpload={onVideoUpload}
            onSubtitleUpload={onSubtitleUpload}
            onRemoveVideo={onRemoveVideo}
            onRemoveSubtitle={onRemoveSubtitle}
            onGenerateSubtitles={onGenerateSubtitles}
            generatingSubtitles={generatingSubtitles}
            isEditing={editingIndex !== null}
            uploadingVideo={uploadingVideo}
            uploadingSubtitle={uploadingSubtitle}
            uploadProgress={uploadProgress}
            removingVideo={removingVideo}
            removingSubtitle={removingSubtitle}
            videoInputRef={videoInputRef}
          />

          {/* Form Actions */}
          <div className="flex gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-3 border-2 border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-slate-900 text-white py-3 rounded-lg font-medium hover:bg-slate-800"
            >
              {editingIndex !== null ? 'Update' : 'Create'} Team Member
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== Bio Fields ====================

const BioFields = ({ formData, setFormData }) => (
  <div className="space-y-3">
    <label className="block text-sm font-medium text-slate-700">Bio (by language)</label>
    <div>
      <label className="block text-xs text-slate-500 mb-1">English</label>
      <textarea
        value={formData.bio?.en || ''}
        onChange={(e) => setFormData(prev => ({ 
          ...prev, 
          bio: { ...prev.bio, en: e.target.value } 
        }))}
        rows={2}
        className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 resize-none"
      />
    </div>
    <div>
      <label className="block text-xs text-slate-500 mb-1">Korean (한국어)</label>
      <textarea
        value={formData.bio?.ko || ''}
        onChange={(e) => setFormData(prev => ({ 
          ...prev, 
          bio: { ...prev.bio, ko: e.target.value } 
        }))}
        rows={2}
        className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 resize-none"
      />
    </div>
    <div>
      <label className="block text-xs text-slate-500 mb-1">Japanese (日本語)</label>
      <textarea
        value={formData.bio?.ja || ''}
        onChange={(e) => setFormData(prev => ({ 
          ...prev, 
          bio: { ...prev.bio, ja: e.target.value } 
        }))}
        rows={2}
        className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 resize-none"
      />
    </div>
  </div>
);

// ==================== Video Upload Section ====================

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VideoUploadSection = ({ 
  formData, 
  setFormData, 
  onVideoUpload, 
  onSubtitleUpload,
  onRemoveVideo,
  onRemoveSubtitle,
  onGenerateSubtitles,
  generatingSubtitles,
  isEditing,
  uploadingVideo,
  uploadingSubtitle,
  uploadProgress,
  videoInputRef,
  removingVideo,
  removingSubtitle
}) => {
  // Check if video is from Object Store (starts with /api/media/)
  const isObjectStoreVideo = formData.video && formData.video.startsWith('/api/media/videos/');
  const videoPreviewUrl = isObjectStoreVideo ? `${BACKEND_URL}${formData.video}` : null;
  
  return (
    <div className="space-y-4 p-4 bg-slate-50 rounded-xl">
      <h3 className="font-medium text-slate-900 flex items-center gap-2">
        <Video className="w-4 h-4" /> Video Introduction
      </h3>
      
      {/* Video Preview */}
      {videoPreviewUrl && (
        <div className="rounded-lg overflow-hidden bg-black">
          <video 
            src={videoPreviewUrl} 
            controls 
            className="w-full max-h-48 object-contain"
            preload="metadata"
          >
            Your browser does not support the video tag.
          </video>
          <div className="bg-slate-800 px-3 py-1.5 text-xs text-slate-300 flex items-center justify-between">
            <span>Current video</span>
            <button
              type="button"
              onClick={onRemoveVideo}
              disabled={removingVideo}
              className={`${removingVideo ? 'text-slate-500' : 'text-red-400 hover:text-red-300'}`}
            >
              {removingVideo ? 'Removing...' : 'Remove'}
            </button>
          </div>
        </div>
      )}
      
      {/* Video URL / Upload */}
      <div>
        <label className="block text-xs text-slate-500 mb-1">Video URL</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={formData.video || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, video: e.target.value }))}
            placeholder="Paste YouTube URL or upload video"
            className="flex-1 px-3 py-2 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
          />
          <label 
            className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-colors ${
              uploadingVideo 
                ? 'bg-slate-300 text-slate-500 cursor-wait' 
                : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
            }`}
          >
            <Upload className="w-4 h-4" />
            {uploadingVideo ? `${uploadProgress}%` : 'Upload'}
            <input 
              ref={videoInputRef}
              type="file" 
              accept="video/*" 
              onChange={(e) => {
                e.stopPropagation();
                onVideoUpload(e);
              }}
              className="hidden"
              disabled={uploadingVideo}
            />
          </label>
        </div>
        {/* Progress bar */}
        {uploadingVideo && (
          <div className="mt-2">
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-slate-700 transition-all duration-300 ease-out"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">Uploading video... {uploadProgress}%</p>
          </div>
        )}
        <p className="text-xs text-slate-400 mt-1">
          {formData.video ? 'Upload a new video to replace the current one' : 'Paste YouTube link or upload (max 100MB)'}
        </p>
      </div>

      {/* AI Subtitle Generation */}
      {isEditing && formData.video && !formData.video.includes('youtube.com') && !formData.video.includes('youtu.be') && (
        <div>
          <button
            type="button"
            onClick={onGenerateSubtitles}
            disabled={generatingSubtitles}
            data-testid="generate-subtitles-btn"
            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
              generatingSubtitles
                ? 'bg-indigo-100 text-indigo-400 cursor-wait'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            {generatingSubtitles ? 'Generating subtitles with AI… this can take a few minutes' : 'Generate Subtitles (AI)'}
          </button>
          <p className="text-xs text-slate-400 mt-1">
            Transcribes English and translates to every registered language. Existing subtitles are never replaced.
          </p>
        </div>
      )}

      {/* Subtitle Uploads */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-slate-500 mb-1 flex items-center gap-1">
            <FileText className="w-3 h-3" /> Korean Subtitle (.vtt)
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={formData.subtitles?.ko || ''}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                subtitles: { ...prev.subtitles, ko: e.target.value } 
              }))}
              placeholder="URL or upload"
              className="flex-1 px-3 py-2 border-2 border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
            <label className="flex items-center px-3 py-2 bg-slate-200 text-slate-700 rounded-lg cursor-pointer hover:bg-slate-300">
              <Upload className="w-4 h-4" />
              <input 
                type="file" 
                accept=".vtt,.srt" 
                onChange={(e) => onSubtitleUpload(e, 'ko')} 
                className="hidden"
                disabled={uploadingSubtitle}
              />
            </label>
            {formData.subtitles?.ko && formData.subtitles.ko.startsWith('/api/media/') && (
              <button
                type="button"
                onClick={() => onRemoveSubtitle('ko')}
                disabled={removingSubtitle}
                className="px-2 py-2 text-red-500 hover:text-red-700 text-xs"
                title="Remove subtitle"
              >
                ✕
              </button>
            )}
          </div>
          {formData.subtitles?.ko && (
            <p className="text-xs text-green-600 mt-1">✓ Subtitle uploaded</p>
          )}
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1 flex items-center gap-1">
            <FileText className="w-3 h-3" /> Japanese Subtitle (.vtt)
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={formData.subtitles?.ja || ''}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                subtitles: { ...prev.subtitles, ja: e.target.value } 
              }))}
              placeholder="URL or upload"
              className="flex-1 px-3 py-2 border-2 border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
            <label className="flex items-center px-3 py-2 bg-slate-200 text-slate-700 rounded-lg cursor-pointer hover:bg-slate-300">
              <Upload className="w-4 h-4" />
              <input 
                type="file" 
                accept=".vtt,.srt" 
                onChange={(e) => onSubtitleUpload(e, 'ja')} 
                className="hidden"
                disabled={uploadingSubtitle}
              />
            </label>
            {formData.subtitles?.ja && formData.subtitles.ja.startsWith('/api/media/') && (
              <button
                type="button"
                onClick={() => onRemoveSubtitle('ja')}
                disabled={removingSubtitle}
                className="px-2 py-2 text-red-500 hover:text-red-700 text-xs"
                title="Remove subtitle"
              >
                ✕
              </button>
            )}
          </div>
          {formData.subtitles?.ja && (
            <p className="text-xs text-green-600 mt-1">✓ Subtitle uploaded</p>
          )}
        </div>
      </div>

      {/* Other language subtitles (AI-generated) */}
      {Object.entries(formData.subtitles || {}).filter(([lang, url]) => url && lang !== 'ko' && lang !== 'ja').length > 0 && (
        <div>
          <label className="block text-xs text-slate-500 mb-2">Other Subtitles (AI-generated)</label>
          <div className="flex flex-wrap gap-2">
            {Object.entries(formData.subtitles || {})
              .filter(([lang, url]) => url && lang !== 'ko' && lang !== 'ja')
              .map(([lang]) => (
                <span
                  key={lang}
                  data-testid={`subtitle-badge-${lang}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-200 rounded-full text-xs font-medium text-slate-700 uppercase"
                >
                  {lang}
                  <button
                    type="button"
                    onClick={() => onRemoveSubtitle(lang)}
                    disabled={removingSubtitle}
                    className="text-slate-500 hover:text-red-600"
                    title={`Remove ${lang} subtitle`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamMemberForm;
